import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import "@/v3.css";
import App from "@/App";
import { RecoveryBoundary, herstelApp } from "@/components/RecoveryBoundary";

// ===== Zelfherstel: gefaalde script/css-loads (verouderde PWA-cache) =====
window.addEventListener("error", (e) => {
  const t = e.target;
  if (t && (t.tagName === "SCRIPT" || t.tagName === "LINK") &&
      !sessionStorage.getItem("bpt_auto_herstel")) {
    sessionStorage.setItem("bpt_auto_herstel", "1");
    herstelApp();
  }
}, true);
// Na succesvolle load: reset de auto-herstel-guard voor toekomstige fouten
window.addEventListener("load", () => {
  setTimeout(() => sessionStorage.removeItem("bpt_auto_herstel"), 10000);
});

// Preserve Revolut OAuth code across auth-redirects (runs BEFORE React routing)
try {
  const params = new URLSearchParams(window.location.search);
  const code = params.get("code");
  const path = window.location.pathname;
  if (code && (path === "/revolut" || path === "/revolut/callback" || path.startsWith("/revolut"))) {
    sessionStorage.setItem("revolut_auth_code", code);
    // Ga direct naar /revolut zodat de code kan worden ingewisseld
    if (path !== "/revolut") {
      window.history.replaceState({}, "", "/revolut?code=" + encodeURIComponent(code));
    }
  }
} catch (e) { /* no-op */ }

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <RecoveryBoundary>
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </RecoveryBoundary>
  </React.StrictMode>,
);

// ===== PWA · Service Worker registratie =====
// Alleen bij production build (create-react-app hot-reload werkt niet met SW).
if ("serviceWorker" in navigator && process.env.NODE_ENV === "production") {
  // Toon "nieuwe versie"-balk zodra een nieuwe SW klaarstaat
  const toonUpdateBalk = (waitingWorker) => {
    if (document.getElementById("bpt-update-banner")) return;
    const bar = document.createElement("div");
    bar.id = "bpt-update-banner";
    bar.setAttribute("data-testid", "update-banner");
    bar.style.cssText =
      "position:fixed;bottom:16px;left:50%;transform:translateX(-50%);z-index:99999;" +
      "background:#0D3B66;color:#fff;padding:12px 18px;border-radius:10px;" +
      "box-shadow:0 8px 30px rgba(0,0,0,.35);display:flex;gap:14px;align-items:center;" +
      "font-family:system-ui,sans-serif;font-size:14px;max-width:92vw;";
    bar.innerHTML =
      "<span>Er is een <b>nieuwe versie</b> van BPTplus beschikbaar.</span>" +
      "<button data-testid='update-banner-refresh' style=\"background:#F26522;color:#fff;border:0;" +
      "border-radius:7px;padding:8px 16px;font-weight:600;cursor:pointer;white-space:nowrap\">Vernieuwen</button>";
    bar.querySelector("button").onclick = () => {
      try { waitingWorker.postMessage("SKIP_WAITING"); } catch { /* val terug op reload */ }
      setTimeout(() => window.location.reload(), 600);
    };
    document.body.appendChild(bar);
  };

  // Auto-reload zodra de nieuwe SW de controle overneemt (1x)
  let herladen = false;
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (herladen) return;
    herladen = true;
    window.location.reload();
  });

  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/service-worker.js")
      .then((reg) => {
        // Al een wachtende SW? Direct balk tonen
        if (reg.waiting && navigator.serviceWorker.controller) toonUpdateBalk(reg.waiting);
        reg.addEventListener("updatefound", () => {
          const nw = reg.installing;
          if (nw) nw.addEventListener("statechange", () => {
            if (nw.state === "installed" && navigator.serviceWorker.controller) {
              toonUpdateBalk(nw);
            }
          });
        });
        // Check elke 30 min op updates (lang openstaande tabs/PWA)
        setInterval(() => reg.update().catch(() => {}), 30 * 60 * 1000);
        // Check ook zodra de app weer op de voorgrond komt (iOS PWA pauzeert timers)
        document.addEventListener("visibilitychange", () => {
          if (document.visibilityState === "visible") {
            reg.update().catch(() => {});
            if (reg.waiting && navigator.serviceWorker.controller) toonUpdateBalk(reg.waiting);
          }
        });
      })
      .catch(() => { /* silent */ });
  });
}
